/** biome-ignore-all lint/nursery/useExplicitType: Zod schema inference */
import { z } from 'zod';
import type { IAPI } from '../../apiResponse';
import { createApiResponseSchema } from '../../apiResponse';
import { challengeTypeSchema } from '../../challenges/enums/challenge-type';
import { cropStatusSchema } from '../../challenges/enums/crop-status';
import { rejectionReasonSchema } from '../../challenges/enums/rejection-reason';
import { triageStatusSchema } from '../../challenges/enums/triage-status';

export const triageSubtitleEntrySchema = z.object({
  index: z.number(),
  startMs: z.number(),
  endMs: z.number(),
  text: z.string(),
  isContext: z.boolean(),
});

export const triageClipSchema = z.object({
  id: z.number(),
  baseLocalPath: z.string().nullable(),
  verticalLocalPath: z.string().nullable().optional(),
  basePath: z.string().nullable(),
  verticalPath: z.string().nullable(),
  verticalRejected: z.number().optional().default(0),
  cropStatus: cropStatusSchema.nullable(),
  vaultPath: z.string().nullable(),
  startMs: z.number().nullable(),
  endMs: z.number().nullable(),
  triageAction: z.string().nullable(),
  rejectionReason: rejectionReasonSchema.nullable(),
  fileSizeKb: z.number().nullable(),
  subtitleEntries: z.array(triageSubtitleEntrySchema).nullable(),
});

export const triageChallengeSchema = z.object({
  id: z.number(),
  title: z.string().nullable(),
  originalTitle: z.string().nullable(),
  definition: z.string().nullable(),
  definitionSimple: z.string().nullable(),
  published: z.number(),
  triageStatus: triageStatusSchema.nullable(),
  type: challengeTypeSchema.nullable(),
  cefrLevel: z.string().nullable(),
  confidenceScore: z.number().nullable(),
  qualityScore: z.number().min(1).max(10).nullable(),
  suggestedForm: z.string().nullable(),
  qualityReasoning: z.string().nullable(),
  totalClips: z.number().default(0),
  approvedClips: z.number().default(0),
});

export const triageItemSchema = z
  .object({
    clip: triageClipSchema,
    challenge: triageChallengeSchema,
  })
  .nullable();

export const getCollectionTriageNextResponseSchema = createApiResponseSchema(
  triageItemSchema,
) as z.ZodSchema<IAPI<ITriageItem | null>>;

export interface ITriageSubtitleEntry {
  index: number;
  startMs: number;
  endMs: number;
  text: string;
  isContext: boolean;
}

export interface ITriageClip {
  id: number;
  baseLocalPath: string | null;
  verticalLocalPath?: string | null;
  basePath: string | null;
  verticalPath: string | null;
  verticalRejected: number;
  cropStatus: string | null;
  vaultPath: string | null;
  startMs: number | null;
  endMs: number | null;
  triageAction: string | null;
  rejectionReason: string | null;
  fileSizeKb: number | null;
  subtitleEntries: ITriageSubtitleEntry[] | null;
}

export interface ITriageChallenge {
  id: number;
  title: string | null;
  originalTitle: string | null;
  definition: string | null;
  definitionSimple: string | null;
  published: number;
  triageStatus: string | null;
  type: string | null;
  cefrLevel: string | null;
  confidenceScore: number | null;
  qualityScore: number | null;
  suggestedForm: string | null;
  qualityReasoning: string | null;
  totalClips: number;
  approvedClips: number;
}

export interface ITriageItem {
  clip: ITriageClip;
  challenge: ITriageChallenge;
}

export type IGetCollectionTriageNextResponse = IAPI<ITriageItem | null>;
